import { Link, type NavigateFn } from '@tanstack/react-router';
import { Edit3, FileText } from 'lucide-react';
import {
  DashboardDataTableColumnKind,
  DashboardTableStatusTone,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import { DemandPartnerPricingType, DemandPartnerStatus, type DemandPartner } from '../../model';

export function createDemandPartnersTableColumns(options: {
  canEdit: boolean;
  navigate: NavigateFn;
  onShowAdsTxt: (partner: DemandPartner) => void;
}) {
  return buildDashboardDataTableColumns<DemandPartner>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: 'Name',
      headerClassName: 'w-64 pl-4',
      className: 'w-64 pl-4 font-medium',
      sortable: true,
      sortKey: 'displayName',
      value: (partner) => (
        <Link
          className={dashboardTableTextActionClassName()}
          to="/demand/partners/$demandPartnerId/details/overview"
          params={{ demandPartnerId: partner.id }}
        >
          {partner.displayName ?? partner.name ?? partner.id}
        </Link>
      ),
    },
    {
      id: 'status',
      kind: DashboardDataTableColumnKind.Status,
      header: 'Status',
      headerClassName: 'w-32',
      className: 'w-32',
      sortable: true,
      sortKey: 'status',
      label: (partner) => statusLabel(partner.status),
      tone: (partner) => statusTone(partner.status),
    },
    textColumn('bidderCode', 'Bidder code', (partner) => partner.bidderCode ?? '-'),
    textColumn('pricing', 'Pricing', pricingLabel),
    textColumn('revenueCap', 'Revenue cap', (partner) =>
      partner.revenueCapEnabled ? `$${String(partner.revenueCap ?? 0)}` : 'Disabled',
    ),
    {
      id: 'adsTxt',
      kind: DashboardDataTableColumnKind.Custom,
      header: 'Ads.txt',
      headerClassName: 'w-24 text-center',
      className: 'w-24 text-center',
      cell: (partner) => (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!partner.adsTxtRecords.length}
          onClick={() => {
            options.onShowAdsTxt(partner);
          }}
        >
          <FileText className="mr-1 h-4 w-4" aria-hidden="true" />
          {partner.adsTxtRecords.length}
        </Button>
      ),
    },
    {
      id: 'connections',
      kind: DashboardDataTableColumnKind.Text,
      header: 'Connections',
      headerClassName: 'w-28',
      className: 'w-28',
      value: (partner) => (
        <Link
          className={dashboardTableTextActionClassName()}
          to="/demand/partners/$demandPartnerId/details/overview"
          params={{ demandPartnerId: partner.id }}
        >
          {String(partner.demandIntegrationCnt ?? partner.demandIntegrationsCnt ?? 0)}
        </Link>
      ),
    },
    {
      id: 'createdOn',
      kind: DashboardDataTableColumnKind.Text,
      header: 'Created',
      headerClassName: 'w-36',
      className: 'w-36',
      sortable: true,
      sortKey: 'createdOn',
      value: (partner) => formatDisplayDate(partner.createdOn, { keepInvalid: true }),
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !options.canEdit,
      cell: (partner) => (
        <DashboardTableRowActions label={dashboardT('demandPartners.actions')}>
          <DashboardTableRowActionItem
            onSelect={() =>
              void options.navigate({
                to: '/demand/partners/edit/$demandPartnerId',
                params: { demandPartnerId: partner.id },
              })
            }
          >
            <Edit3 className="h-4 w-4" aria-hidden="true" />
            Edit
          </DashboardTableRowActionItem>
        </DashboardTableRowActions>
      ),
    },
  ]);
}

function textColumn(
  id: string,
  header: string,
  value: (partner: DemandPartner) => React.ReactNode,
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text,
    header,
    headerClassName: 'w-44',
    className: 'w-44 truncate',
    value,
  } as const;
}

function statusLabel(status: DemandPartnerStatus) {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function statusTone(status: DemandPartnerStatus) {
  if (status === DemandPartnerStatus.Active) return DashboardTableStatusTone.Success;
  if (status === DemandPartnerStatus.Archived) return DashboardTableStatusTone.Neutral;
  return DashboardTableStatusTone.Warning;
}

function pricingLabel(partner: DemandPartner) {
  if (!partner.pricingType) return 'No cost';
  if (partner.pricingType === DemandPartnerPricingType.Cpm) {
    return `CPM · $${String(partner.ioCpmCostValue ?? 0)}`;
  }
  const label =
    partner.pricingType === DemandPartnerPricingType.ProfitShare ? 'Profit Share' : 'Rev Share';
  return `${label} · ${String(partner.revShareValue ?? 0)}%`;
}
